import {AnimationItem} from 'lottie-web/build/player/lottie_light';
import {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import LIKE_EFFECT_LIGHT from '../../../public/static/viewer/like-effect-light.json';
import LIKE_EFFECT from '../../../public/static/viewer/like-effect.json';
import LIKE_FIRST_LIGHT from '../../../public/static/viewer/like-first-light.json';
import LIKE_FIRST from '../../../public/static/viewer/like-first.json';
import LIKE_MORE_LIGHT from '../../../public/static/viewer/like-more-light.json';
import LIKE_MORE from '../../../public/static/viewer/like-more.json';
import Lottie from '../../common/component/Lottie';
import Text from '../../common/component/Text';
import {X_PADDING_ONLY_SMALL_CN} from '../../common/constant';
import {pToast} from '../../common/container/PToast';
import {showAuthModal} from '../../common/function';
import useEffectOnChange from '../../common/hook/useEffectOnChange';
import useLogin from '../../common/hook/useLogin';
import useTheme from '../../common/hook/useTheme';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {useFetchInfo} from '../../common/util/fetch';
import {formatNumber} from '../../common/util/number';
import {sendViewerEvent} from '../common';
import {actions, ActionType} from '../state/actions';
import {IViewType} from '../type';

interface IProps {
  viewType: IViewType;
}

export default function Like({viewType}: IProps) {
  const {t} = useTranslation();
  const {isLightMode} = useTheme();
  const disaptch = useDispatch();
  const [contentId, episodeId, episodeLike] = useTypedSelector(({viewer}) => [
    viewer.episode?.contentId,
    viewer.episode?.id,
    viewer.viewerEnd?.episodeLike,
  ]);
  const {totalLikeCount, myLikeCount} = episodeLike || {
    totalLikeCount: 0,
    myLikeCount: 0,
  };
  const thumbFirstRef = useRef<AnimationItem>();
  const thumbMoreRef = useRef<AnimationItem>();
  const effectRef = useRef<AnimationItem>();
  const rollbackCountRef = useRef(myLikeCount);
  const {fetchStatus} = useFetchInfo(ActionType.FetchLikeEpisode, episodeId);
  const [showMoreThumb, setShowMoreThumb] = useState(myLikeCount > 0);
  useEffectOnChange(() => {
    if (fetchStatus === 'success') rollbackCountRef.current = myLikeCount;
  }, fetchStatus);

  const {isLogin} = useLogin();
  const onClickThumb = (e: EventObject) => {
    e.stopPropagation();

    if (!isLogin) {
      showAuthModal();
      return;
    }

    if (contentId === undefined || episodeId === undefined) return;
    if (myLikeCount >= MAX_LIKE_COUNT) {
      pToast({
        desc: _t => _t('viewer_end_like_snackbar'),
        variant: 'black',
      });
      return;
    }

    setShowMoreThumb(myLikeCount > 0);

    const thumbAni =
      myLikeCount === 0 ? thumbFirstRef.current : thumbMoreRef.current;
    thumbAni?.goToAndPlay(0);
    effectRef.current?.goToAndPlay(0);

    sendViewerEvent({
      actionName: 'like',
      action: 'click',
      customProps: {
        content_id: contentId,
        episode_id: episodeId,
        like_value: TRACKING_LIKE_VALUE[myLikeCount],
        event_layer: 'like',
        viewer_type: viewType,
      },
    });

    disaptch(
      actions.fetchLikeEpisode({
        contentId,
        episodeId,
        likeCount: myLikeCount + 1,
        rollbackCount: rollbackCountRef.current,
      })
    );
  };

  const onEffectComplete = () => {
    effectRef.current?.goToAndStop(0);
  };

  return (
    <div
      className={cn(
        'relative flex h-[184px] justify-center overflow-hidden',
        X_PADDING_ONLY_SMALL_CN
      )}
    >
      <Text
        className={cn(
          'absolute top-42 light:text-black',
          totalLikeCount > 0 ? 's21-bold-white' : 's14-semibold-white'
        )}
      >
        {totalLikeCount > 0
          ? formatNumber(totalLikeCount)
          : t('viewer_end_like_first')}
      </Text>
      <Lottie
        className="absolute w-60 h-30 top-20"
        lottieRef={effectRef}
        animationData={isLightMode ? LIKE_EFFECT_LIGHT : LIKE_EFFECT}
        autoplay={false}
        onComplete={onEffectComplete}
      />
      <Lottie
        className={cn('absolute w-80 h-80 top-72', {
          hidden: showMoreThumb,
        })}
        lottieRef={thumbFirstRef}
        animationData={isLightMode ? LIKE_FIRST_LIGHT : LIKE_FIRST}
        autoplay={false}
        onClick={onClickThumb}
      />
      <Lottie
        className={cn('absolute w-80 h-80 top-72', {
          hidden: !showMoreThumb,
        })}
        lottieRef={thumbMoreRef}
        animationData={isLightMode ? LIKE_MORE_LIGHT : LIKE_MORE}
        autoplay={false}
        onClick={onClickThumb}
      />
    </div>
  );
}

const MAX_LIKE_COUNT = 5;

const TRACKING_LIKE_VALUE: {[key: number]: string} = {
  0: 'good',
  1: 'nice',
  2: 'great',
  3: 'best',
  4: 'perfect',
};
